<template>
  <div
    class="dropdown-select"
    tabindex="0"
    v-on:blur="searchable ? null : (open = false)"
  >
    <div
      class="select-menu-button"
      :class="{ 'button-open': open, disabled: disabled, bordered: bordered }"
      color="#ffffff"
      @click="openDropdown"
    >
      <div class="button-inner">
        <template v-if="searchable">
          <span
            v-if="!open"
            class="menu-title"
            :class="{ placeholder: selectedValue == null }"
            >{{ getSelectedOptionsText }}</span
          >
          <input
            v-on:blur="open = false"
            v-else
            class="search-input"
            v-model="searchKeyword"
            ref="input"
            @click.stop.prevent
          />
        </template>
        <template v-else>
          <span
            class="menu-title"
            :class="{ placeholder: selectedValue == null }"
            >{{ getSelectedOptionsText }}</span
          >
        </template>

        <div class="icon-area">
          <feather-icon
            v-if="clearable && selectedValue != null"
            class="view-icon"
            icon="XIcon"
            svgClasses="h-4 w-4"
            @click.stop="onClearButtonClick"
          />
          <feather-icon
            class="arrow-icon"
            :class="{ open: open }"
            icon="ChevronDownIcon"
            svgClasses="h-4 w-4"
          />
        </div>
      </div>
    </div>
    <transition name="fade">
      <template>
        <div
          ref="selectItemContainer"
          class="select-item-container"
          :class="selectItemContainerClass"
        >
          <div
            class="select-item"
            @click="setValue(option.value)"
            v-for="(option, index) in filteredSelectOptions"
            :key="index"
          >
            <feather-icon
              v-if="option.featherIconName"
              class="custom-feather-icon"
              :icon="option.featherIconName"
              svgClasses="h-4 w-4"
            />
            {{ option.text }}
          </div>

          <div
            v-if="filteredSelectOptions.length == 0"
            class="select-item"
            @click="open = false"
          >
            選択肢なし
          </div>
        </div>
      </template>
    </transition>
  </div>
</template>

<script>
import { HtmlUtil } from "@/utils";

const DROPDOWN_POSITIONS = Object.freeze({
  TOP: "top",
  BOTTOM: "bottom",
});

export default {
  name: "CrewDropdownSelect",
  props: {
    placeholder: {
      type: String,
      default: "",
    },
    clearable: {
      type: Boolean,
      required: false,
      default: false,
    },
    bordered: {
      type: Boolean,
      required: false,
      default: true,
    },
    onSelect: {
      type: Function,
      required: false,
      default: () => {},
    },
    selectedValue: {
      required: true,
    },
    selectOptions: {
      type: Array,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      open: false,
      searchKeyword: "",
    };
  },
  computed: {
    getSelectedOptionsText() {
      const option = this.selectOptions.find(
        item => item.value === this.selectedValue
      );
      if (option) {
        return option.text;
      } else {
        return this.placeholder;
      }
    },
    filteredSelectOptions() {
      return this.selectOptions.filter(element => {
        return element.text.includes(this.searchKeyword);
      });
    },
    selectItemContainerClass() {
      const dropdownPosition = this.dropdownPosition;
      return {
        visible: this.open,
        [dropdownPosition]: true,
      }; // { visible: boolean, top: true } | { visible: true, bottom: true }
    },
    dropdownPosition() {
      if (this.open && this.isBottomOverflow()) {
        return DROPDOWN_POSITIONS.TOP;
      }
      return DROPDOWN_POSITIONS.BOTTOM; // 通常は下に表示
    },
  },
  methods: {
    onClearButtonClick() {
      this.$emit("update:selectedValue", null);
      this.open = false;
      this.onSelect(null);
    },
    onClickDropdownButton() {
      if (this.searchable && this.open) return;
      this.openDropdown();
    },
    openDropdown() {
      if (!this.disabled) {
        this.open = !this.open;
        if (this.searchable) {
          this.searchKeyword = "";

          this.$nextTick(() => {
            this.$refs.input.focus();
          });
        }
      }
    },
    // ドロップダウンのリストが画面の下にはみ出るか判定
    isBottomOverflow() {
      const {
        $refs: { selectItemContainer },
      } = this;
      const result = HtmlUtil.isBottomOverflow(selectItemContainer); // boolean;
      return result;
    },
    setValue(value) {
      this.$emit("update:selectedValue", value);
      this.open = false;
      this.onSelect(value);
    },
  },
};
</script>

<style lang="scss" scoped>
.dropdown-select {
  position: relative;
  height: fit-content;
  min-width: 150px;
  user-select: none;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.select-menu-button {
  height: 39px;
  border-radius: 6px;
  border: solid 1px #fff;
  background: #fff;
  color: #777;
  padding: 8px 13px !important;
  transition: all 300ms 0s ease;
  cursor: pointer;
  font-size: 14px;
  &.bordered {
    border: solid 1px #cacaca;
  }
  &:hover {
    box-shadow: 0 2px 8px -2px #777;
  }
  &.disabled {
    cursor: not-allowed;
    background: #ececec;
    &:hover {
      box-shadow: 0 0 0 0 #777;
    }
  }
}
.button-open {
  border-radius: 4px 4px 0px 0px;
  border-bottom: solid 1px #e0e0e0;
}
.menu-title {
  margin-right: 5px;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  &.placeholder {
    color: #999999;
  }
}
.select-item-container {
  position: absolute;
  z-index: 1;
  background: white;
  width: 100%;
  max-height: 185px;
  overflow-y: auto;
  // デフォルトで選択肢は非表示にし、クリック時のみ表示する
  // getBoundingClientRect()でDOM位置を取得するため、v-showでなくvisibilityで非表示にする
  visibility: hidden;

  border-radius: 0 0 0.5rem 0.5rem;
  box-shadow: 0px 4px 25px 0px rgba(0, 0, 0, 0.1);

  &.top {
    bottom: 100%;
  }
  &.bottom {
    top: 100%;
  }
  &.visible {
    visibility: visible;
  }
}
.select-item {
  cursor: pointer;
  padding: 8px 13px;
  display: flex;
  .custom-feather-icon {
    margin-right: 12px;
  }
}
.select-item:hover {
  background: rgba(45, 156, 219, 0.2);
  color: #2d9cdb;
  transition: 0.2s;
}
.button-inner {
  height: 21px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.arrow-icon {
  margin-left: auto;
  transition: all 300ms 0s ease;
}
.icon-area {
  display: flex;
  .view-icon {
    margin-right: 3px;
    cursor: pointer;
  }
}
.open {
  transform: rotateZ(-180deg);
}
.search-input {
  border: none;
  outline: none;
  font-size: 14px;
  max-width: calc(100% - 40px);
}
</style>
